import { Info } from "lucide-react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../../../../components/ui/hover-card";

interface ConclusionHoverCardProps {
  /**
   * Called each time the card opens. The caller gates its detail fetch on this,
   * because the badge cell ships only the slim summary, which does not carry the
   * conclusion text.
   */
  onOpen: () => void;
  isLoading: boolean;
  /**
   * The verdict's conclusion as its wrapper resolved it, or null when the
   * verdict carries none.
   */
  conclusion: string | null;
  /** What the card says when the verdict carries no conclusion. */
  emptyMessage: string;
  /** A line under the conclusion that renders whether or not it has loaded. */
  footnote?: string;
}

/**
 * Hover-revealed "i" affordance on a verdict cell. Hovering it opens a card
 * showing the same Conclusion the Workpaper Review panel shows for that
 * verdict. Moving the mouse off the icon dismisses it.
 *
 * Shared by every column kind that has one, so the affordance cannot look or
 * behave differently depending on which engine produced the verdict; each
 * kind's wrapper owns its own fetch, its own resolution, and anything it says
 * beyond this.
 *
 * The conclusion arrives resolved rather than being derived here, because both
 * the field it lives on and the standing text for its absence are kind-specific:
 * an attribute test's is the auditor trail's L1, with fixed advice when it
 * cannot conclude, while an analytics verdict has no trail at all and reads its
 * own summary fields. Deriving here would have to pick one kind's source and
 * wording and apply it to the other, which is what makes a cell disagree with
 * the review pane beside it.
 */
export const ConclusionHoverCard = ({
  onOpen,
  isLoading,
  conclusion,
  emptyMessage,
  footnote,
}: ConclusionHoverCardProps) => {
  return (
    <HoverCard
      openDelay={100}
      closeDelay={0}
      onOpenChange={(next) => {
        if (next) {
          onOpen();
        }
      }}
    >
      <HoverCardTrigger asChild>
        <button
          type="button"
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
          onClick={(e) => e.stopPropagation()}
          aria-label="View conclusion"
        >
          <Info className="h-4 w-4 text-gray-500" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        className="w-80 p-3 max-h-96 overflow-y-auto"
        align="start"
      >
        <div className="space-y-2">
          <h4 className="font-medium text-sm">Conclusion</h4>
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : conclusion ? (
            <p className="text-sm text-gray-600 whitespace-pre-wrap">
              {conclusion}
            </p>
          ) : (
            <p className="text-sm text-gray-500">{emptyMessage}</p>
          )}
          {footnote !== undefined && (
            <p className="text-xs text-font-secondary">{footnote}</p>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
